/**
 * Écriture — « **Le script qui tombe** » : le pattern et le barème côté client (⟦C52 · A-170 §1⟧).
 *
 * Des blocs-phrases tombent un par un dans un plateau de colonnes (Accroche, Histoire, Chute,
 * Appel) ; le joueur pousse chacun dans la bonne, de plus en plus vite. Ce module ne porte PLUS de
 * barème ni de physique : la suite de blocs (`genererBlocs`), le barème (`noterBlocs`) et la
 * mécanique du plateau (`manche.ts`) vivent dans `@maxime/shared`, un seul code que le serveur
 * (autoritatif) et le bac à sable font tourner. Il ne reste ici que ce qui est propre au client :
 *
 *  - `depuisServeur` : le pattern servi par `GET /atelier-pattern`, traduit tel quel. **Le client
 *    ne tire rien** (SPEC-UI §4.1) : suite, colonnes attendues, vitesses viennent du fil ;
 *  - `genererLocal` : le mode dégradé du **bac à sable** (aucun serveur), qui appelle le même
 *    `genererBlocs` sur les phrases de `content/ecriture.json` (importé : ce sont des mots que le joueur
 *    lit) et le RNG du moteur. Les TABLES de difficulté, elles, sont celles de `atelier.json`, que le
 *    client n'importe pas exprès (`parite-client.test.ts`) : elles sont recopiées dans `TABLES`, comme
 *    pour les quatre autres mini-jeux, et `tables.test.ts` fait échouer toute dérive. Ce n'est pas un
 *    deuxième avis : en mode compte, il ne sert jamais.
 *
 * Ce module reste **pur** (aucun son, aucune horloge) : `sons.test.ts` le garde.
 */

import {
  ATTENTE_DEBUT_MS,
  COLONNES,
  EcritureFile,
  compterPiegesEtDoublets,
  draw,
  dureeDeLaManche,
  exigenceAtelier,
  genererBlocs,
  generateAtelierPattern,
  noterBlocs,
  type BlocEcriture,
  type ColonneId,
  type GameState,
  type PlacementEcriture,
  type ResultatBlocs,
  type VideoFormat,
} from "@maxime/shared";

import brutEcriture from "@content/ecriture.json";

import type { PatternEcritureApi } from "../../api/atelier";

/** La manche telle que l'écran la joue, d'où qu'elle vienne. */
export interface PatternScript {
  colonnes: readonly ColonneId[];
  blocs: readonly BlocEcriture[];
  /** La durée de la manche : le dernier bloc a touché le sol, plus un souffle. */
  dureeMs: number;
  /** *Plume d'or* : blocs ratés pardonnés au barème. */
  erreursPardonnees: number;
  pieges: number;
  doublets: number;
  /** Variante posée (§7.4) : les blocs descendent par pas, la manche est plus lente. */
  pose: boolean;
  /** Exigence E du palier (pour l'intro). */
  k: number;
  /** Borne d'échelle : 100, ou 85 en tolérance et en mode posé (§7.3, §7.4). */
  scoreCap: number;
}

/** Plafond du mode posé, comme la tolérance (SPEC-UI §7.4 ✎). */
export const PLAFOND_POSE = 85;
/** Le mode posé ralentit la manche de ce facteur (jumeau de `ECRITURE_POSE_TEMPO`, serveur). */
export const TEMPO_POSE = 1.6;
/** Les fenêtres de la tolérance (SPEC-UI §7.3), jumelles de `TOLERANCE_FENETRE`. */
const TOLERANCE_FENETRE = 1.3;

/**
 * Le pattern **servi par le serveur**, traduit tel quel. Traduction pure : rien n'est recalculé,
 * complété ni « corrigé » (SPEC-UI §4.1).
 *
 * ⚠️ **Le `scoreCap` du fil n'est PAS celui du barème d'écran** : l'enveloppe du serveur fusionne
 * la tolérance et le plafond de compétence ⟦A-9⟧, que le moteur applique déjà à la publication.
 * Le laisser raboter le score du mini-jeu l'appliquerait deux fois. On ne retient que la borne
 * qui ne dépend de rien (100, 85 en tolérance ou mode posé), comme le fait le serveur.
 */
export function depuisServeur(api: PatternEcritureApi, state: GameState): PatternScript {
  return {
    colonnes: api.params.colonnes,
    blocs: api.params.blocs,
    dureeMs: api.params.dureeMs,
    erreursPardonnees: api.params.erreursPardonnees,
    pieges: api.params.pieges,
    doublets: api.params.doublets,
    pose: api.reducedMotion,
    k: api.tier,
    scoreCap: capDeLEchelle(state, api.reducedMotion),
  };
}

function capDeLEchelle(state: GameState, pose: boolean): number {
  const cap = exigenceAtelier(state).score_cap;
  return pose ? Math.min(PLAFOND_POSE, cap) : cap;
}

export interface OptionsScript {
  state: GameState;
  format: VideoFormat;
  seed: string;
  pose: boolean;
  /** Les assists de l'écriture possédés (`assists(state, "fil_rouge")`). */
  assists?: Readonly<Record<string, number>>;
}

/**
 * Les tables de `content/atelier.json` (`minijeux.fil_rouge`), recopiées pour le bac à sable. Une
 * dérive est refusée par `pattern.test.ts` (qui relit le fichier) : changer un chiffre côté contenu
 * sans le changer ici fait rougir la CI.
 */
export const TABLES = {
  dureeLongueMs: 42_000,
  dureeShortMs: 30_000,
  chuteDepartMs: [5700, 4900, 4500, 4150, 3850, 3600],
  chuteFinMs: [1750, 1500, 1380, 1250, 1130, 1000],
  /** ⟦C56 · A-173⟧ La courbe de montée (ease-in) et le souffle qui raccourcit, jumeaux de `fil_rouge`. */
  courbeExposant: 2.8,
  pauseMs: 600,
  pauseFinMs: [320, 240, 200, 170, 150, 130],
  colonnesParPalier: [3, 3, 4, 4, 4, 4],
  piegesParPalier: [0, 0, 0, 1, 2, 3],
  doubletsParPalier: [0, 0, 0, 0, 2, 3],
  /** `scaling.paliers` de `atelier.json`. */
  paliers: [100, 1000, 10000, 100000, 500000],
} as const;
const PHRASES = EcritureFile.parse(brutEcriture).phrases;
const PALIERS = TABLES.paliers;

/** Où en est la chaîne DANS son palier, de 0 à 1 (jumeau de `avancementDansLePalier`, serveur). */
function avancementDansLePalier(abonnes: number, E: number): number {
  const niveau = Math.max(0, Math.min(5, Math.trunc(E)));
  if (niveau < 1 || niveau >= 5) return 0;
  const bas = PALIERS[niveau - 1];
  const haut = PALIERS[niveau];
  if (bas === undefined || haut === undefined || bas <= 0 || haut <= bas) return 0;
  const f = Math.log(Math.max(abonnes, bas) / bas) / Math.log(haut / bas);
  return Math.max(0, Math.min(1, f));
}

/** Une table E0..E5 lue au palier `E`, tirée vers le suivant de `f`. */
function parPalier(table: readonly number[], E: number, f: number): number {
  const i = Math.max(0, Math.min(table.length - 1, Math.trunc(E)));
  const a = table[i]!;
  const b = table[Math.min(table.length - 1, i + 1)] ?? a;
  return a + (b - a) * Math.max(0, Math.min(1, f));
}

/**
 * Le pattern du **bac à sable** : le même `genererBlocs` que le serveur, sur le contenu de
 * `atelier.json`, avec le RNG du moteur (`draw`). Mode dégradé : en compte, c'est le pattern servi
 * qui joue.
 */
export function genererLocal(options: OptionsScript): PatternScript {
  const { state, format, seed, pose } = options;
  const a = options.assists ?? {};
  const exigence = exigenceAtelier(state);
  const E = exigence.k;
  const f = avancementDansLePalier(state.subscribers, E);
  const tolerance = state.flags.accessibility_tolerance === true;
  const court = format === "short";

  const tempo =
    (1 + (a.chrono_bonus_pct ?? 0) / 100) * (tolerance ? TOLERANCE_FENETRE : 1) * (pose ? TEMPO_POSE : 1);
  const budgetBase = court ? TABLES.dureeShortMs : TABLES.dureeLongueMs;
  const partDeManche = budgetBase / TABLES.dureeLongueMs;
  const colonnes = COLONNES.slice(0, Math.max(3, Math.round(parPalier(TABLES.colonnesParPalier, E, f))));

  const graine = generateAtelierPattern(state, "ecriture", seed).seed_component;
  let n = 0;
  const blocs = genererBlocs(
    {
      colonnes,
      budgetMs: Math.round(budgetBase * tempo),
      chuteDepartMs: parPalier(TABLES.chuteDepartMs, E, f) * tempo,
      chuteFinMs: parPalier(TABLES.chuteFinMs, E, f) * tempo,
      courbe: TABLES.courbeExposant,
      pauseMs: TABLES.pauseMs * tempo,
      pauseFinMs: parPalier(TABLES.pauseFinMs, E, f) * tempo,
      attenteMs: ATTENTE_DEBUT_MS * tempo,
      pieges: Math.round(parPalier(TABLES.piegesParPalier, E, f) * partDeManche),
      doublets: Math.round(parPalier(TABLES.doubletsParPalier, E, f) * partDeManche),
      premierOffert: (a.hook_toujours_premier ?? 0) >= 1,
      histoiresOffertes: a.beats_preplaces ?? 0,
    },
    PHRASES,
    { next: () => draw(graine, 0, "atelier_pattern", n++) },
  );

  return {
    colonnes,
    blocs,
    dureeMs: dureeDeLaManche(blocs),
    erreursPardonnees: a.erreurs_pardonnees ?? 0,
    ...compterPiegesEtDoublets(blocs),
    pose,
    k: E,
    scoreCap: capDeLEchelle(state, pose),
  };
}

export interface ResultatScript extends ResultatBlocs {
  /** Le score affiché : celui du barème, borné par l'échelle (85 en tolérance ou mode posé). */
  scoreAffiche: number;
}

/**
 * Le barème de l'écran : **`noterBlocs`, celui du serveur**, plus la borne d'échelle. Le serveur
 * note pareil (`validate.ts` : `Math.min(plafond, score)`), donc l'écran de fin ne contredit pas
 * la note publiée.
 */
export function noterScript(
  pattern: PatternScript,
  placements: readonly PlacementEcriture[],
): ResultatScript {
  const r = noterBlocs(pattern.blocs, pattern.colonnes, placements, pattern.erreursPardonnees);
  return { ...r, scoreAffiche: Math.max(0, Math.min(pattern.scoreCap, r.score)) };
}

/**
 * ⟦C56 · A-173⟧ **La chaleur de la manche**, de 0 (calme) à 1 (le dernier bloc, le plus rapide) : le
 * signal de montée, sans texte. Elle se lit des blocs SERVIS, jamais d'une règle recopiée : la vitesse
 * d'un bloc est `chuteMs` du premier ÷ la sienne, et la chaleur est sa part du chemin vers la vitesse du
 * dernier bloc. Entre l'apparition de deux blocs elle glisse en ligne droite dans le temps (sans quoi
 * la jauge ne bougerait qu'à chaque bloc), et elle ne redescend jamais, puisque la chute ne remonte pas.
 *
 * Décorative : aucun barème ne la lit, aucune timeline ne l'embarque (même invariant que le
 * tremblement de la santé mentale, `useTremblementSM`).
 */
export function chaleurDeLaManche(blocs: readonly BlocEcriture[], tMs: number): number {
  const premier = blocs[0];
  const dernier = blocs.at(-1);
  if (!premier || !dernier || blocs.length < 2) return 0;
  const vitesseMax = premier.chuteMs / Math.max(1, dernier.chuteMs);
  if (vitesseMax <= 1) return 0;
  const chaleurDe = (b: BlocEcriture): number =>
    Math.max(0, Math.min(1, (premier.chuteMs / Math.max(1, b.chuteMs) - 1) / (vitesseMax - 1)));
  if (tMs <= premier.apparitionMs) return 0;
  if (tMs >= dernier.apparitionMs) return 1;
  // Le dernier bloc apparu, et le suivant : on glisse de l'un à l'autre. Un doublet (même `chuteMs`,
  // apparition rapprochée) ne casse rien : l'écart de chaleur est nul.
  let k = 0;
  while (k + 1 < blocs.length - 1 && blocs[k + 1]!.apparitionMs <= tMs) k++;
  const a = blocs[k]!;
  const b = blocs[k + 1]!;
  const ecart = Math.max(1, b.apparitionMs - a.apparitionMs);
  const part = Math.max(0, Math.min(1, (tMs - a.apparitionMs) / ecart));
  return chaleurDe(a) + (chaleurDe(b) - chaleurDe(a)) * part;
}

/** ⟦C56⟧ Le dernier tiers de la manche : c'est là que la chute s'emballe, et que le puits se met à trembler. */
export const DERNIER_TIERS = 2 / 3;
